import type { SequenceDetailStep } from "@unbox/api";
import {
  ArrowSquareDown,
  ArrowSquareUp,
  Clock,
  DotsVertical,
  Edit05,
  MessageTextSquare02,
  Plus,
  SlashCircle01,
  Stop,
  Trash02,
} from "@untitledui/icons";
import { useState } from "react";
import { useMessageTemplates } from "@/api/message-templates";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cx } from "@/utils/cx";
import { AutomatedMessageDialog, type AutomatedMessageValue } from "./automated-message-dialog";

// Frontend-local step type. UI treats "wait" as a first-class step type alongside the two
// action types. At the persistence boundary, collapseForPersist folds waits into the next
// action's delayDays so the backend (which only knows action types) remains unchanged.
export type StepType = "send_message" | "remove_from_collab" | "wait";

export type Step = {
  step: number;
  type: StepType;
  delayDays: number;
  sendAtHour: number | null;
  sendAtTimezone: string | null;
  messageTemplateId: string | null;
  customMessage: string | null;
} & Pick<SequenceDetailStep, "imageId" | "imageUrl">;

type PersistedStep = Omit<Step, "type"> & {
  type:
    | "send_message"
    | "remove_from_collab"
    | "remove_from_linked_collab"
    | "remove_from_gmv_max"
    | "add_to_gmv_max_campaign"
    | "block_creator"
    | "notify_tiktok"
    | "branch"
    | "resolve_risk";
  branch?: "taken" | "not_taken" | null;
  branchCondition?: "video_removed" | "metadata_edited" | null;
};

type StepInput = {
  step: number;
  type: "send_message" | "remove_from_collab";
  delayDays: number;
  sendAtHour: number | null;
  sendAtTimezone: string | null;
  messageTemplateId: string | null;
  customMessage: string | null;
} & Pick<SequenceDetailStep, "imageId">;

type Template = { id: string; name: string; category: string; content: string };

interface StepBuilderProps {
  steps: Step[];
  onStepsChange: (steps: Step[]) => void;
  emptyMessage?: string;
}

const DELAY_OPTIONS = Array.from({ length: 31 }, (_, i) => i);

// Expand persisted steps (where delayDays lives on the action step) into the UI shape:
// every action is preceded by its own wait step.
// This linear builder predates branching: branch steps and the steps on their arms are
// skipped (and dropped on save). Branched sequences need the dedicated branch UI.
export function expandPersistedSteps(persisted: PersistedStep[]): Step[] {
  const out: Step[] = [];
  let idx = 0;
  for (const s of persisted) {
    // This linear builder only renders send_message / remove_from_collab actions.
    // Branch steps, steps on branch arms, and the newer remove_from_linked_collab /
    // remove_from_gmv_max / block_creator / notify_tiktok / resolve_risk actions belong to
    // branched sequences edited via the dedicated policy UI.
    if (
      s.type === "branch" ||
      s.type === "remove_from_linked_collab" ||
      s.type === "remove_from_gmv_max" ||
      s.type === "add_to_gmv_max_campaign" ||
      s.type === "block_creator" ||
      s.type === "notify_tiktok" ||
      s.type === "resolve_risk" ||
      s.branch != null
    )
      continue;
    out.push({
      step: idx++,
      type: "wait",
      delayDays: s.delayDays,
      sendAtHour: null,
      sendAtTimezone: null,
      messageTemplateId: null,
      customMessage: null,
      imageId: null,
      imageUrl: null,
    });
    out.push({
      step: idx++,
      type: s.type,
      delayDays: 0,
      sendAtHour: s.sendAtHour,
      sendAtTimezone: s.sendAtTimezone,
      messageTemplateId: s.messageTemplateId,
      customMessage: s.customMessage,
      imageId: s.imageId,
      imageUrl: s.imageUrl,
    });
  }
  return out;
}

// Collapse UI steps into the persisted shape. Consecutive waits accumulate into the next
// action's delayDays. Trailing waits with no following action are dropped.
export function collapseForPersist(steps: Step[]): StepInput[] {
  const out: StepInput[] = [];
  let pendingDelay = 0;
  for (const s of steps) {
    if (s.type === "wait") {
      pendingDelay += s.delayDays;
      continue;
    }
    out.push({
      step: out.length,
      type: s.type,
      delayDays: pendingDelay,
      sendAtHour: s.sendAtHour,
      sendAtTimezone: s.sendAtTimezone,
      messageTemplateId: s.messageTemplateId,
      customMessage: s.customMessage,
      imageId: s.imageId,
    });
    pendingDelay = 0;
  }
  return out;
}

function formatSendAtTime(step: Step) {
  if (step.sendAtHour === null) return null;
  const period = step.sendAtHour >= 12 ? "PM" : "AM";
  const hour = step.sendAtHour % 12 || 12;
  return `${hour}:00 ${period}`;
}

function StepMoreMenu({
  onMoveUp,
  onMoveDown,
  onDelete,
  canMoveUp,
  canMoveDown,
}: {
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Step options"
          className="flex items-center justify-center w-6 h-6 rounded p-1.5 text-tertiary hover:bg-secondary cursor-pointer"
        >
          <DotsVertical className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        <DropdownMenuItem onClick={onMoveUp} disabled={!canMoveUp}>
          <ArrowSquareUp className="w-4 h-4 mr-3 text-secondary" />
          <span className="text-sm font-semibold text-secondary">Move step up</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onMoveDown} disabled={!canMoveDown}>
          <ArrowSquareDown className="w-4 h-4 mr-3 text-secondary" />
          <span className="text-sm font-semibold text-secondary">Move step down</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onDelete}>
          <Trash02 className="w-4 h-4 mr-3 text-error-600" />
          <span className="text-sm font-semibold text-error-600">Delete step</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function WaitStepCard({
  step,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: {
  step: Step;
  onChange: (next: Step) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const days = step.delayDays;

  return (
    <div className="group flex items-center gap-3 py-3 w-full">
      <div className="flex items-center justify-center w-8 h-8 rounded-full border border-secondary bg-primary text-tertiary shrink-0 z-10">
        <Clock className="w-4 h-4" />
      </div>
      <div className="flex items-center gap-3 rounded-md border border-secondary bg-tertiary pl-4 pr-1 py-2">
        <div className="flex items-center gap-1">
          {editing ? (
            <>
              <span className="text-xs font-medium text-primary">Wait</span>
              <select
                value={days}
                onChange={(e) => {
                  onChange({ ...step, delayDays: Number(e.target.value) });
                  setEditing(false);
                }}
                onBlur={() => setEditing(false)}
                className="rounded-full border border-secondary bg-primary px-2 py-0.5 text-xs font-bold text-primary focus:outline-none focus:ring-2 focus:ring-brand/80"
              >
                {DELAY_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              <span className="text-xs font-medium text-primary">business {days === 1 ? "day" : "days"}</span>
            </>
          ) : days === 0 ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs font-medium text-primary cursor-pointer hover:underline"
              aria-label="Edit delay"
            >
              Immediately
            </button>
          ) : (
            <>
              <span className="text-xs font-medium text-primary">Wait</span>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="flex items-center justify-center min-w-6 h-6 rounded-full border border-secondary bg-primary text-xs font-bold text-primary px-1.5 cursor-pointer hover:bg-secondary"
                aria-label="Edit delay"
                data-testid="delay-days"
              >
                {days}
              </button>
              <span className="text-xs font-medium text-primary">business {days === 1 ? "day" : "days"}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label="Edit delay"
            className="flex items-center justify-center w-6 h-6 rounded p-1.5 text-tertiary hover:text-secondary cursor-pointer"
          >
            <Edit05 className="w-4 h-4" />
          </button>
          <StepMoreMenu
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onDelete={onRemove}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
          />
        </div>
      </div>
    </div>
  );
}

function SendMessageStepCard({
  step,
  index,
  templates,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: {
  step: Step;
  index: number;
  templates: Template[];
  onUpdate: (next: Step) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  const [editOpen, setEditOpen] = useState(false);
  const template = step.messageTemplateId ? templates.find((t) => t.id === step.messageTemplateId) : null;
  const content = template?.content ?? step.customMessage ?? "";
  const hasContent = !!(template || step.customMessage);

  const handleSave = (value: AutomatedMessageValue) => {
    onUpdate({
      ...step,
      messageTemplateId: value.messageTemplateId,
      customMessage: value.customMessage,
      imageId: value.imageId,
      imageUrl: value.imageUrl,
      sendAtHour: value.sendAtHour,
      sendAtTimezone: value.sendAtTimezone,
    });
  };

  return (
    <div className="relative flex items-start gap-3 w-full">
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-secondary-solid border border-primary text-white text-sm font-semibold shrink-0 z-10">
        {index + 1}
      </div>
      <div className="flex flex-col flex-1 min-w-0 rounded-md overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 bg-primary border border-secondary rounded-t-md">
          <MessageTextSquare02 className="w-5 h-5 text-tertiary" />
          <span className="text-sm font-semibold text-primary flex-1">Send Message</span>
          {formatSendAtTime(step) && (
            <span className="text-xs text-tertiary flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatSendAtTime(step)}
            </span>
          )}
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            aria-label="Edit step"
            className="flex items-center justify-center w-6 h-6 rounded p-1.5 text-tertiary hover:text-secondary cursor-pointer"
          >
            <Edit05 className="w-4 h-4" />
          </button>
          <StepMoreMenu
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onDelete={onRemove}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
          />
        </div>
        <button
          type="button"
          onClick={() => setEditOpen(true)}
          className="flex flex-col gap-1 px-4 py-3 bg-primary border-x border-b border-secondary rounded-b-md text-left cursor-pointer hover:bg-secondary transition-colors"
        >
          {hasContent ? (
            <>
              <p className="text-xs font-medium text-quaternary truncate">
                {template ? `Template: ${template.name}` : "Custom Message"}
              </p>
              <p className="text-sm font-medium text-tertiary truncate">{content}</p>
            </>
          ) : (
            <p className="text-sm italic text-utility-warning-600">Empty Message — click to edit</p>
          )}
        </button>
      </div>
      <AutomatedMessageDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        initialValue={{
          messageTemplateId: step.messageTemplateId,
          customMessage: step.customMessage,
          imageId: step.imageId,
          imageUrl: step.imageUrl,
          sendAtHour: step.sendAtHour,
          sendAtTimezone: step.sendAtTimezone,
        }}
        submitLabel="Save"
        onSubmit={handleSave}
      />
    </div>
  );
}

function RemoveStepCard({
  index,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: {
  index: number;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  return (
    <div className="relative flex items-start gap-3 w-full">
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-secondary-solid border border-primary text-white text-sm font-semibold shrink-0 z-10">
        {index + 1}
      </div>
      <div className="flex items-center gap-3 flex-1 min-w-0 rounded-md border border-secondary bg-primary px-4 py-3">
        <SlashCircle01 className="w-5 h-5 text-tertiary" />
        <span className="text-sm font-semibold text-primary flex-1">Remove from Collaboration</span>
        <StepMoreMenu
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          onDelete={onRemove}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
        />
      </div>
    </div>
  );
}

function AddStepMenu({ onAdd }: { onAdd: (type: StepType) => void }) {
  return (
    <div className="flex flex-col items-center w-full pt-2 pb-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Add step"
            className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-solid text-white shadow hover:opacity-90 transition-opacity cursor-pointer z-10"
          >
            <Plus className="w-4 h-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-[260px]">
          <DropdownMenuItem onClick={() => onAdd("wait")}>
            <Clock className="w-5 h-5 mr-2 text-tertiary" />
            <span className="text-base font-medium text-primary">Add time Delay</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onAdd("send_message")}>
            <MessageTextSquare02 className="w-5 h-5 mr-2 text-tertiary" />
            <span className="text-base font-medium text-primary">Send Automated Message</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onAdd("remove_from_collab")}>
            <SlashCircle01 className="w-5 h-5 mr-2 text-tertiary" />
            <span className="text-base font-medium text-primary">Remove from Collaboration</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function DividerLabel({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 h-5 w-full pt-0">
      {label && <span className="text-sm font-medium text-quaternary z-10 py-2 bg-secondary_alt">{label}</span>}
      <div className="flex-1 h-px bg-utility-gray-200" />
    </div>
  );
}

function EndCard() {
  return (
    <div className="flex flex-col gap-3 w-full">
      <DividerLabel label="End" />
      <div className="flex items-center gap-3 w-full">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-secondary-solid border border-primary text-white shrink-0 z-10">
          <Stop className="w-4 h-4" />
        </div>
        <div className="flex items-center flex-1 rounded-md border border-secondary bg-tertiary px-4 py-3">
          <span className="text-base font-medium text-primary">End Automation</span>
        </div>
      </div>
    </div>
  );
}

function makeNewStep(type: StepType, nextIndex: number): Step {
  if (type === "wait") {
    return {
      step: nextIndex,
      type: "wait",
      delayDays: 3,
      sendAtHour: null,
      sendAtTimezone: null,
      messageTemplateId: null,
      customMessage: null,
      imageId: null,
      imageUrl: null,
    };
  }
  if (type === "remove_from_collab") {
    return {
      step: nextIndex,
      type: "remove_from_collab",
      delayDays: 0,
      sendAtHour: null,
      sendAtTimezone: null,
      messageTemplateId: null,
      customMessage: null,
      imageId: null,
      imageUrl: null,
    };
  }
  return {
    step: nextIndex,
    type: "send_message",
    delayDays: 0,
    sendAtHour: null,
    sendAtTimezone: null,
    messageTemplateId: null,
    customMessage: null,
    imageId: null,
    imageUrl: null,
  };
}

function renderSteps({
  steps,
  templates,
  onUpdate,
  onRemove,
  onMove,
}: {
  steps: Step[];
  templates: Template[];
  onUpdate: (i: number, next: Step) => void;
  onRemove: (i: number) => void;
  onMove: (i: number, dir: -1 | 1) => void;
}) {
  let actionIndex = 0;
  return steps.map((step, i) => {
    const canMoveUp = i > 0;
    const canMoveDown = i < steps.length - 1;
    const moveUp = () => onMove(i, -1);
    const moveDown = () => onMove(i, 1);
    switch (step.type) {
      case "wait":
        return (
          <WaitStepCard
            key={step.step}
            step={step}
            onChange={(next) => onUpdate(i, next)}
            onRemove={() => onRemove(i)}
            onMoveUp={moveUp}
            onMoveDown={moveDown}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
          />
        );
      case "send_message": {
        const idx = actionIndex++;
        return (
          <SendMessageStepCard
            key={step.step}
            step={step}
            index={idx}
            templates={templates}
            onUpdate={(next) => onUpdate(i, next)}
            onRemove={() => onRemove(i)}
            onMoveUp={moveUp}
            onMoveDown={moveDown}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
          />
        );
      }
      case "remove_from_collab": {
        const idx = actionIndex++;
        return (
          <RemoveStepCard
            key={step.step}
            index={idx}
            onRemove={() => onRemove(i)}
            onMoveUp={moveUp}
            onMoveDown={moveDown}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
          />
        );
      }
    }
  });
}

function useStepOps(steps: Step[], onStepsChange: (next: Step[]) => void) {
  const nextIndex = () => steps.reduce((max, s) => Math.max(max, s.step), -1) + 1;

  const append = (s: Step) => onStepsChange([...steps, s]);

  const appendAction = (s: Step) => {
    const last = steps[steps.length - 1];
    let idx = nextIndex();
    const toAppend: Step[] = [];
    if (!last || last.type !== "wait") {
      toAppend.push({
        step: idx++,
        type: "wait",
        delayDays: 0,
        sendAtHour: null,
        sendAtTimezone: null,
        messageTemplateId: null,
        customMessage: null,
        imageId: null,
        imageUrl: null,
      });
    }
    toAppend.push({ ...s, step: idx });
    onStepsChange([...steps, ...toAppend]);
  };
  const update = (i: number, next: Step) => onStepsChange(steps.map((s, j) => (j === i ? next : s)));
  const remove = (i: number) => onStepsChange(steps.filter((_, j) => j !== i).map((s, j) => ({ ...s, step: j })));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    onStepsChange(next);
  };

  return { nextIndex, append, appendAction, update, remove, move };
}

export function SequenceCanvas({
  steps,
  onStepsChange,
  triggerEventLabel,
  triggerSupporting,
  triggerIcon,
}: StepBuilderProps & {
  triggerEventLabel: string;
  triggerSupporting?: string;
  triggerIcon?: React.ReactNode;
}) {
  const { data: templates = [] } = useMessageTemplates();
  const [pendingMessageOpen, setPendingMessageOpen] = useState(false);
  const ops = useStepOps(steps, onStepsChange);

  const handleAdd = (type: StepType) => {
    if (type === "send_message") {
      setPendingMessageOpen(true);
      return;
    }
    if (type === "wait") {
      ops.append(makeNewStep("wait", ops.nextIndex()));
      return;
    }
    ops.appendAction(makeNewStep(type, ops.nextIndex()));
  };

  const handleNewMessage = (value: AutomatedMessageValue) => {
    const base = makeNewStep("send_message", ops.nextIndex());
    ops.appendAction({ ...base, ...value });
  };

  return (
    <div className="sequence-steps relative flex flex-col gap-3 w-full max-w-[559px]">
      <DividerLabel label="Trigger" />
      <div className="flex items-center gap-3 w-full">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-secondary-solid border border-primary text-white shrink-0 z-10">
          {triggerIcon ?? <span className="text-xs">▶</span>}
        </div>
        <div className="flex items-center gap-3 flex-1 rounded-md border border-secondary bg-tertiary px-4 py-3 min-w-0">
          <div className="flex flex-col flex-1 min-w-0">
            <p className="text-sm font-semibold text-primary truncate">{triggerEventLabel}</p>
            {triggerSupporting && <p className="text-sm text-tertiary truncate">{triggerSupporting}</p>}
          </div>
        </div>
      </div>

      {steps.length > 0 && <DividerLabel label="Start Automation" />}

      {renderSteps({ steps, templates, onUpdate: ops.update, onRemove: ops.remove, onMove: ops.move })}

      <AddStepMenu onAdd={handleAdd} />

      <EndCard />

      <AutomatedMessageDialog
        open={pendingMessageOpen}
        onOpenChange={setPendingMessageOpen}
        onSubmit={handleNewMessage}
      />
    </div>
  );
}

// Compat wrapper for callers that render the builder inside a smaller surface.
export function StepBuilder({ steps, onStepsChange, emptyMessage }: StepBuilderProps) {
  const { data: templates = [] } = useMessageTemplates();
  const [pendingMessageOpen, setPendingMessageOpen] = useState(false);
  const ops = useStepOps(steps, onStepsChange);

  const handleAdd = (type: StepType) => {
    if (type === "send_message") {
      setPendingMessageOpen(true);
      return;
    }
    if (type === "wait") {
      ops.append(makeNewStep("wait", ops.nextIndex()));
      return;
    }
    ops.appendAction(makeNewStep(type, ops.nextIndex()));
  };

  const handleNewMessage = (value: AutomatedMessageValue) => {
    const base = makeNewStep("send_message", ops.nextIndex());
    ops.appendAction({ ...base, ...value });
  };

  return (
    <div className="flex flex-col w-full gap-3">
      {steps.length === 0 && emptyMessage && <p className="text-sm text-tertiary text-center pt-4">{emptyMessage}</p>}
      {renderSteps({ steps, templates, onUpdate: ops.update, onRemove: ops.remove, onMove: ops.move })}
      <AddStepMenu onAdd={handleAdd} />
      <AutomatedMessageDialog
        open={pendingMessageOpen}
        onOpenChange={setPendingMessageOpen}
        onSubmit={handleNewMessage}
      />
    </div>
  );
}
